import { Howl, Howler } from 'howler'
import { isIOS, isAndroid } from '../helpers/features'
import { state } from './audioState'

export function applyMute(): void {
  Howler.mute(state.isMutedManually)
}

export function setMutedManually(muted: boolean): void {
  state.isMutedManually = muted
  applyMute()
}

export function setGlobalVolume(volume: number): void {
  Howler.volume(volume)
}

export function safeFadeTo(sound: Howl, toVolume: number, ms: number): void {
  if (!sound) return

  if (isIOS || isAndroid) {
    // iOS has issues with Howler's fade, use manual fade with setInterval for more reliable timing
    const fromVolume = sound.volume()
    const volumeDiff = toVolume - fromVolume

    // If already at target volume, no need to fade
    if (Math.abs(volumeDiff) < 0.001) {
      sound.volume(toVolume)
      return
    }

    const startTime = performance.now()
    const intervalMs = 16 // ~60fps updates

    const fadeInterval = setInterval(() => {
      const elapsed = performance.now() - startTime
      const progress = Math.min(elapsed / ms, 1)
      const currentVolume = fromVolume + volumeDiff * progress

      try {
        sound.volume(currentVolume)
      } catch (e) {
        // Sound might be unloaded, stop fading
        clearInterval(fadeInterval)
        return
      }

      if (progress >= 1) {
        clearInterval(fadeInterval)
        sound.volume(toVolume) // Ensure final volume is exact
      }
    }, intervalMs)
  } else {
    sound.fade(sound.volume(), toVolume, ms)
  }
}

export function stopActiveOfType(type: string): void {
  const entry = state.active[type]
  if (!entry) return
  entry.sound.stop()
  entry.sound.unload()
  state.active[type] = null
}

export function stopSoundOfType(type: string): void {
  stopActiveOfType(type)
}

export function startSound(
  type: string,
  basePath: string,
  name: string,
  params: { html5?: boolean; loop?: boolean; volume?: number; fadeMs?: number } = {},
): Howl {
  stopActiveOfType(type)

  const volume = params.volume ?? 1
  const loop = params.loop ?? true
  const html5 = params.html5 ?? false
  const fadeMs = params.fadeMs ?? 500

  const sound = new Howl({
    src: [`${basePath}${name}.mp3`, `${basePath}${name}.ogg`],
    autoplay: false,
    loop,
    volume: 0, // fade in from 0
    html5,
  })

  // Play the sound immediately - Howler.autoUnlock will handle iOS/Android context unlocking
  // The first sound played in response to user interaction will unlock the context
  // On iOS/Android, we need to ensure the sound actually starts playing to unlock the context
  if ((isIOS || isAndroid) && sound.state() === 'unloaded') {
    // Sound needs to load first - wait for load then play
    sound.once('load', () => {
      sound.play()
      safeFadeTo(sound, volume, fadeMs)
    })
    sound.load() // Start loading
  } else {
    // Sound is loaded or not iOS - play immediately
    sound.play()
    safeFadeTo(sound, volume, fadeMs)
  }

  state.active[type] = { sound, volume }
  return sound
}
